import { Link } from "@inertiajs/react"
import { MessageCircle } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { useUnreadMessagesCount } from "@/hooks/use-unread-messages-count"
import { messagesPath } from "@/routes"

/**
 * OKS-37 — Creator-shell header trigger (next to bell).
 *
 * Renders a `<MessageCircle>` link to `/messages` with an unread-count
 * badge. Accessible label is the localised count (UX Spec §6 — color is
 * not the sole indicator).
 *
 * OKS-598 — `variant` picks the layout:
 *   - `"icon"` (default): the 36px icon-only trigger used inline at ≥sm.
 *   - `"row"`: a full-width, ≥44px menu row (icon + label + badge in ONE
 *     Link) for the collapsed "…" overflow menu below sm, so the whole row
 *     is the tap target. The count-aware `aria-label` is preserved so the
 *     unread count stays in the accessible name.
 */
export function CreatorMessagesHeaderTrigger({
  variant = "icon",
}: {
  variant?: "icon" | "row"
} = {}) {
  const { t } = useTranslation()
  const count = useUnreadMessagesCount()
  const badge = count > 99 ? "99+" : count
  const ariaLabel =
    count > 0
      ? `${t("messaging.creator.nav")} (${count} unread)`
      : t("messaging.creator.nav")

  if (variant === "row") {
    return (
      <Link
        href={messagesPath()}
        className="text-foreground hover:bg-accent hover:text-accent-foreground relative flex h-11 w-full items-center gap-2 rounded-md px-2"
        aria-label={ariaLabel}
        data-testid="messaging-creator-header-trigger"
      >
        <MessageCircle className="size-5" aria-hidden="true" />
        <span className="text-sm font-medium">
          {t("messaging.creator.nav")}
        </span>
        {count > 0 && (
          <Badge
            variant="default"
            className="ml-auto h-4 min-w-4 px-1 text-[10px]"
            data-testid="messaging-creator-header-badge"
          >
            {badge}
          </Badge>
        )}
      </Link>
    )
  }

  return (
    <Link
      href={messagesPath()}
      className="text-muted-foreground hover:bg-accent hover:text-foreground relative inline-flex size-9 items-center justify-center rounded-md"
      aria-label={ariaLabel}
      data-testid="messaging-creator-header-trigger"
    >
      <MessageCircle className="size-5" aria-hidden="true" />
      {count > 0 && (
        <Badge
          variant="default"
          className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px]"
          data-testid="messaging-creator-header-badge"
        >
          {badge}
        </Badge>
      )}
    </Link>
  )
}
